import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { v4 as uuidv4 } from 'uuid';

const Survey = () => {
    const [questions, setQuestions] = useState([]);
    const [speakers, setSpeakers] = useState([]);
    const [answers, setAnswers] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const textGradientStyle = {
        background: 'linear-gradient(to right, #033f83, #079acf, #65bade)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        color: 'transparent'
    };

    const buttonGradientStyle = {
        background: 'linear-gradient(to right, #033f83, #079acf, #65bade)'
    };

    useEffect(() => {
        fetchData();
        if (!localStorage.getItem('survey_anonymous_id')) {
            localStorage.setItem('survey_anonymous_id', uuidv4());
        }
    }, []);

    const fetchData = async () => {
        try {
            const [qRes, sRes] = await Promise.all([
                api.get('/surveys/questions'),
                api.get('/speakers')
            ]);
            setQuestions(qRes.data);
            setSpeakers(sRes.data);
            
            const initial = {};
            qRes.data.forEach(q => {
                if (q.type === 'multiple_choice') initial[q.id] = [];
                else initial[q.id] = '';
                
                if (q.is_mixed) {
                    initial[`${q.id}_sub`] = '';
                }
            });
            setAnswers(initial);

            // Track entry
            const anonymousId = localStorage.getItem('survey_anonymous_id');
            const userId = JSON.parse(localStorage.getItem('user'))?.id;
            api.post('/surveys/track', {
                anonymous_id: anonymousId,
                user_id: userId,
                event_type: 'entry'
            }).catch(e => console.error("Tracking error", e));

        } catch (error) {
            console.error("Error fetching survey data", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAnswerChange = (questionId, value, type) => {
        if (type === 'multiple_choice') {
            const current = [...(answers[questionId] || [])];
            if (current.includes(value)) {
                setAnswers({ ...answers, [questionId]: current.filter(v => v !== value) });
            } else {
                setAnswers({ ...answers, [questionId]: [...current, value] });
            }
        } else {
            setAnswers({ ...answers, [questionId]: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const anonymousId = localStorage.getItem('survey_anonymous_id');

        const formattedAnswers = questions.map(q => {
            let answerText = Array.isArray(answers[q.id]) ? answers[q.id].join(', ') : answers[q.id];
            
            if (q.is_mixed && answers[`${q.id}_sub`]) {
                answerText = `${answerText} | Comentario: ${answers[`${q.id}_sub`]}`;
            }

            return {
                question_id: q.id,
                text: answerText,
                answer_type: q.type
            };
        }).filter(ans => ans.text !== undefined && ans.text !== null && ans.text !== '');

        try {
            await api.post('/surveys/submit', {
                anonymous_id: anonymousId,
                answers: formattedAnswers
            });
            setIsSubmitted(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            console.error("Error submitting survey", error);
            alert("Error al enviar la encuesta. Por favor intenta de nuevo.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-white/10 border-t-purple-500 rounded-full animate-spin"></div>
            </div>
        );
    }

    if (isSubmitted) {
        return (
            <div className="max-w-2xl mx-auto py-20 px-6 text-center animate-fadeIn">
                <div className="w-24 h-24 bg-emerald-500/20 rounded-[40px] flex items-center justify-center mx-auto mb-8 border border-emerald-500/30">
                    <span className="text-4xl">🎉</span>
                </div>
                <h1 className="text-4xl font-black text-white uppercase italic tracking-tighter mb-4">¡Muchas Gracias!</h1>
                <p className="text-white/60 text-lg leading-relaxed mb-10">Tu opinión es fundamental para nosotros. Seguiremos trabajando para ofrecerte la mejor experiencia académica.</p>
                <button 
                    onClick={() => window.location.href = '/'}
                    className="px-10 py-4 bg-white/10 hover:bg-white/20 border border-white/10 rounded-[30px] text-white font-black text-xs uppercase tracking-widest transition-all"
                >
                    Volver al Inicio
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto py-12 px-6 animate-fadeIn pb-32">
            <header className="mb-12 text-center">
                <h1 className="text-4xl sm:text-5xl font-black uppercase italic tracking-tighter mb-4" style={{...textGradientStyle, fontFamily: 'Drina, cursive'}}>Encuesta de Satisfacción</h1>
                <p className="text-[10px] font-black uppercase tracking-[0.4em]" style={textGradientStyle}>Ayúdanos a mejorar tu experiencia</p>
            </header>

            <form onSubmit={handleSubmit} className="space-y-10">
                {questions.map((q, idx) => (
                    <div key={q.id} className="bg-white border border-slate-100 p-8 rounded-[40px] shadow-lg transition-all hover:shadow-xl">
                        <div className="mb-6">
                            <h3 className="text-lg font-bold flex gap-4" style={textGradientStyle}>
                                <span style={{ WebkitTextFillColor: 'initial' }} className="text-blue-600">0{idx + 1}</span>
                                {q.text}
                            </h3>
                            {q.speaker_id && (
                                <div className="mt-4 flex items-center gap-3 bg-slate-50 w-max p-2 pr-6 rounded-2xl border border-slate-200">
                                    {speakers.find(s => s.id === q.speaker_id)?.photo_url ? (
                                        <img 
                                            src={speakers.find(s => s.id === q.speaker_id).photo_url} 
                                            className="w-10 h-10 rounded-xl object-cover" 
                                            alt="Speaker" 
                                        />
                                    ) : (
                                        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xs" style={buttonGradientStyle}>🧠</div>
                                    )}
                                    <div>
                                        <p className="text-[8px] font-black uppercase tracking-widest" style={textGradientStyle}>Evaluando a:</p>
                                        <p className="text-xs font-bold text-slate-800">{speakers.find(s => s.id === q.speaker_id)?.name} {speakers.find(s => s.id === q.speaker_id)?.last_name}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Rating 1-5 or 1-10 */}
                        {(q.type === 'rating_5' || q.type === 'rating_10') && (
                            <div className="space-y-6">
                                <div className="flex flex-wrap gap-2">
                                    {[...Array(q.type === 'rating_5' ? 5 : 10)].map((_, i) => {
                                        const val = i + 1;
                                        const isSelected = answers[q.id] === val;
                                        return (
                                            <button
                                                key={val}
                                                type="button"
                                                onClick={() => handleAnswerChange(q.id, val, q.type)}
                                                className={`w-10 h-10 sm:w-12 sm:h-12 rounded-2xl font-black transition-all border ${isSelected ? 'text-white shadow-lg scale-110 border-transparent' : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-white hover:text-slate-800'}`}
                                                style={isSelected ? buttonGradientStyle : {}}
                                            >
                                                {val}
                                            </button>
                                        );
                                    })}
                                </div>
                                {q.is_mixed && (
                                    <div className="animate-fadeIn space-y-3">
                                        <p className="text-[10px] font-black uppercase tracking-widest ml-1" style={textGradientStyle}>{q.sub_question_text || 'Comentarios adicionales'}</p>
                                        <textarea
                                            className="w-full bg-slate-50 border border-slate-200 p-4 rounded-3xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all min-h-[80px] text-sm shadow-inner"
                                            placeholder="Tu opinión nos importa..."
                                            value={answers[`${q.id}_sub`]}
                                            onChange={(e) => setAnswers({...answers, [`${q.id}_sub`]: e.target.value})}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Rating NPS 0-10 */}
                        {q.type === 'nps' && (
                            <div className="space-y-6">
                                <div className="flex flex-wrap gap-1 sm:gap-2 justify-center">
                                    {[...Array(11)].map((_, i) => {
                                        const val = i;
                                        const isSelected = answers[q.id] === val;
                                        return (
                                            <button
                                                key={val}
                                                type="button"
                                                onClick={() => handleAnswerChange(q.id, val, q.type)}
                                                className={`flex-1 min-w-[32px] max-w-[48px] h-10 sm:h-12 rounded-xl font-black transition-all border ${isSelected ? 'text-white shadow-lg scale-110 border-transparent' : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-white hover:text-slate-800'}`}
                                                style={isSelected ? buttonGradientStyle : {}}
                                            >
                                                {val}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="flex justify-between text-[9px] sm:text-[10px] uppercase tracking-widest font-bold px-1 text-center mt-2" style={textGradientStyle}>
                                    <span className="max-w-[40%] text-left">0: Para nada probable</span>
                                    <span className="max-w-[40%] text-right">10: Extremadamente probable</span>
                                </div>
                                {q.is_mixed && (
                                    <div className="animate-fadeIn space-y-3">
                                        <p className="text-[10px] font-black uppercase tracking-widest ml-1" style={textGradientStyle}>{q.sub_question_text || 'Comentarios adicionales'}</p>
                                        <textarea
                                            className="w-full bg-slate-50 border border-slate-200 p-4 rounded-3xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all min-h-[80px] text-sm shadow-inner"
                                            placeholder="Tu opinión nos importa..."
                                            value={answers[`${q.id}_sub`]}
                                            onChange={(e) => setAnswers({...answers, [`${q.id}_sub`]: e.target.value})}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Text Answer */}
                        {q.type === 'text' && (
                            <textarea
                                className="w-full bg-slate-50 border border-slate-200 p-6 rounded-3xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all min-h-[120px] shadow-inner"
                                placeholder="Escribe tu respuesta aquí..."
                                value={answers[q.id]}
                                onChange={(e) => handleAnswerChange(q.id, e.target.value, q.type)}
                                required={q.required}
                            />
                        )}

                        {/* Single Choice */}
                        {q.type === 'single_choice' && (
                            <div className="grid grid-cols-1 gap-3">
                                {q.options?.map((opt, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => handleAnswerChange(q.id, opt, q.type)}
                                        className={`p-4 rounded-2xl text-left text-sm transition-all border ${answers[q.id] === opt ? 'border-transparent text-white shadow-md' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-white'}`}
                                        style={answers[q.id] === opt ? buttonGradientStyle : {}}
                                    >
                                        <span className={`mr-3 inline-block w-3 h-3 rounded-full border-2 ${answers[q.id] === opt ? 'border-white bg-white' : 'border-slate-300'}`}></span>
                                        {opt}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Multiple Choice */}
                        {q.type === 'multiple_choice' && (
                            <div className="grid grid-cols-1 gap-3">
                                {q.options?.map((opt, i) => {
                                    const isSelected = answers[q.id]?.includes(opt);
                                    return (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => handleAnswerChange(q.id, opt, q.type)}
                                            className={`p-4 rounded-2xl text-left text-sm transition-all border ${isSelected ? 'border-transparent text-white shadow-md' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-white'}`}
                                            style={isSelected ? buttonGradientStyle : {}}
                                        >
                                            <span className={`mr-3 inline-block w-4 h-4 rounded border-2 flex items-center justify-center ${isSelected ? 'border-white bg-white' : 'border-slate-300'}`}>
                                                {isSelected && <span className="text-[10px]" style={textGradientStyle}>✓</span>}
                                            </span>
                                            {opt}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ))}

                <div className="pt-6">
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        style={buttonGradientStyle}
                        className={`w-full py-6 rounded-[40px] font-black uppercase tracking-[0.3em] text-white shadow-xl transition-all transform active:scale-95 hover:scale-[1.02] ${isSubmitting ? 'opacity-70 animate-pulse' : ''}`}
                    >
                        {isSubmitting ? 'Enviando Respuestas...' : 'Enviar Encuesta'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Survey;
